import React from 'react';
import { arrayOf, bool, shape, string, objectOf } from 'prop-types';
import { dt } from 'yoda-core-components/lib/helpers/Utils/GetTailwindToken';
import Icon from 'yoda-core-components/lib/components/Icon';
import List from 'yoda-core-components/lib/components/List/List';
import classNames from 'classnames/bind';
import LinkConnected from '../../../Link/Link';
import { helpSectionIconMapping } from '../../YodaFooterIconMapping';

const cx = classNames.bind();
/**
 *HelpSection component to standardize the styling and html structure of HelpSection on footer
 * This imports config file which has the data for helpList
 * This component also consumes List and Icon from core component
 * */

const HelpSection = ({ deviceType: { isDesktop = true }, footerHelpSection, listClass }) => {
    const onHelpLinkClicked = (event, helpLinkPath, newTab) => {
        event.preventDefault();

        return {
            openInNewTab: newTab === '_blank',
            url: helpLinkPath,
        };
    };

    const helpLinkClasses = dt([
        'block',
        'flex',
        'items-center',
        'md:text-center',
        'mx-2',
        'my-0',
        'text-left',
        'w-full',
        'xl:mx-5',
    ]);

    const helpSectionRenderer = (dataItem, index) => {
        const {
            id = '',
            linkPath = '',
            linkPathMobile = '',
            newTab = false,
            text = '',
            textStrong = '',
        } = dataItem;
        const ariaLabelId = `helpSection${id}`;
        const shouldGiveCall = !isDesktop && id === 'give_call';
        const helpLinkPath = shouldGiveCall ? linkPathMobile : linkPath;
        const isEmailLink = id === 'email_us';

        return (
            <LinkConnected
                aria-labelledby={ariaLabelId}
                className={`${helpLinkClasses} ${cx({
                    'cursor-default pointer-events-none': !(
                        (shouldGiveCall && linkPathMobile) ||
                        linkPath
                    ),
                })}`}
                data-automation-id={`help-link-${id}`}
                href={isEmailLink ? '' : helpLinkPath}
                id={`help-link-${id}`}
                linkName={`footer:${text.toLowerCase()}`}
                onClick={(event) => onHelpLinkClicked(event, helpLinkPath, newTab)}
                target={newTab}
                triggerAnalytics
            >
                <span data-automation-id="help-section-icon-block">
                    <Icon
                        automationId={`help-icon-${index}`}
                        className={dt(['text-black'])}
                        height="32"
                        width="32"
                    >
                        {helpSectionIconMapping[dataItem.name]}
                    </Icon>
                </span>
                <span
                    className={dt(['ml-4'])}
                    data-automation-id="help-sectiontext-block"
                    id={ariaLabelId}
                    key={id}
                >
                    <span className={dt(['block', 'text-left', 'text-xs'])}>{text}</span>
                    <span
                        className={dt(['block', 'text-left', 'text-sm', 'font-semibold'])}
                        data-automation-id={`help-text-${index}`}
                    >
                        {textStrong}
                    </span>
                </span>
            </LinkConnected>
        );
    };

    return (
        <div
            className={dt(['block', 'text-black', 'sm:w-[175px]', 'm-auto'])}
            data-automation-id="help-section-container"
        >
            <List
                automationId="help-list"
                childRenderer={helpSectionRenderer}
                datasource={footerHelpSection}
                id="footer"
                itemStyleClass={listClass}
                listBodyClass="overflow-hidden"
                spacing="None"
                listStyleClass={dt(['!flex', 'flex-col', 'gap-y-4'])}
            />
        </div>
    );
};

HelpSection.defaultProps = {
    deviceType: { isDesktop: true },
    listClass: '',
};

HelpSection.propTypes = {
    footerHelpSection: arrayOf(
        shape({
            id: string,
            linkPathMobile: string,
            linkPath: string,
            text: string,
            textStrong: string,
            newTab: bool,
        })
    ).isRequired,
    deviceType: objectOf(bool),
    listClass: string,
};

export default HelpSection;
